import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useAccount } from './useAccount'
import { API_BASE_URL } from '../constants/system'
import type {
  LlmUsageFilters,
  LlmUsageListResult,
  LlmUsageScope,
  LlmUsageSummary,
} from '../types/llmUsage'

/**
 * One hook for the whole cost report, replacing four that each fetched "model
 * work, scoped somehow": useTokenUsage (which fired SIX parallel queries for one
 * page), useAutocoderThroughput, usePipelineQueue and the lane census.
 *
 * The scope picks the URL, and the URL is what decides — server-side — whether
 * cost comes back. Nothing here can widen a scope: the job and parent faces put
 * customer/job in the PATH, where the handler overrides any filter that tried
 * to say otherwise.
 *
 * Polling discipline follows useLlmLanes: `enabled` is the mount gate so a tab
 * nobody is looking at makes no request, and `refetchIntervalInBackground:
 * false` stops a backgrounded browser tab polling forever.
 */

const scopeURL = (scope: LlmUsageScope, resource: '' | '/summary'): string => {
  if (scope.kind === 'admin') return `${API_BASE_URL}/admin/llm-usage${resource || '/'}`
  return (
    `${API_BASE_URL}/autocoder/${encodeURIComponent(scope.customer)}` +
    `/${encodeURIComponent(scope.job)}/llm-usage${resource || '/'}`
  )
}

/**
 * Filters as query params. A `parent` scope pins client_ref here rather than
 * letting the caller pass it, so a drill-down cannot accidentally show another
 * parent's calls.
 */
const toParams = (scope: LlmUsageScope, filters: LlmUsageFilters): Record<string, string> => {
  const merged: LlmUsageFilters = { ...filters }
  if (scope.kind === 'parent') merged.client_ref = scope.clientRef

  const params: Record<string, string> = {}
  const put = (key: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return
    params[key] = String(value)
  }
  put('customer', scope.kind === 'admin' ? merged.customer : undefined)
  put('job', scope.kind === 'admin' ? merged.job : undefined)
  put('user_id', merged.user_id)
  put('feature', merged.feature)
  if (merged.features?.length) params.features = merged.features.join(',')
  put('model', merged.model)
  put('request_type', merged.request_type)
  put('lane', merged.lane)
  put('state', merged.state)
  put('client_ref', merged.client_ref)
  put('team_id', merged.team_id)
  // Admin mounts only in practice: the job-scoped handler strips a label
  // grouping server-side regardless of what is sent.
  put('groupBy', merged.group_by)
  put('start_time', merged.start_time)
  put('end_time', merged.end_time)
  put('limit', merged.limit)
  put('offset', merged.offset)
  return params
}

/**
 * The cache key. Scope segments lead so an admin looking at everything and a
 * customer looking at one job can never share an entry — those are different
 * answers, and one of them contains money.
 */
const usageKey = (
  scope: LlmUsageScope,
  resource: string,
  params: Record<string, string>,
) => [
  'llm-usage',
  scope.kind,
  scope.kind === 'admin' ? '' : scope.customer,
  scope.kind === 'admin' ? '' : scope.job,
  resource,
  params,
]

export interface UseLlmUsageResult {
  rows: LlmUsageListResult | undefined
  summary: LlmUsageSummary | undefined
  loading: boolean
  isFetching: boolean
  error: string | null
  refetch: () => void
}

export const useLlmUsage = (
  scope: LlmUsageScope,
  filters: LlmUsageFilters = {},
  active = true,
  pollSeconds = 0,
): UseLlmUsageResult => {
  const account = useAccount()
  const params = toParams(scope, filters)

  // A job or parent face with no scope to name is not a request worth making.
  const scoped = scope.kind === 'admin' ? true : !!scope.customer && !!scope.job
  const enabled = active && scoped && !!account.user

  // 0 means "do not poll" — the default. The report is history, not a live
  // gauge; the lane census beside it is the thing that needs a heartbeat.
  const refetchInterval = pollSeconds > 0 ? Math.max(5, pollSeconds) * 1000 : (false as const)

  const listQuery = useQuery<LlmUsageListResult, Error>({
    queryKey: usageKey(scope, 'list', params),
    queryFn: async () => {
      const res = await axios.get<LlmUsageListResult>(scopeURL(scope, ''), {
        headers: { Accept: 'application/json' },
        params,
      })
      return res.data
    },
    enabled,
    refetchInterval,
    refetchIntervalInBackground: false,
    staleTime: 5 * 1000,
  })

  // The summary deliberately ignores limit/offset: it describes the whole
  // filtered set, not the page being looked at.
  const summaryParams = { ...params }
  delete summaryParams.limit
  delete summaryParams.offset

  const summaryQuery = useQuery<LlmUsageSummary, Error>({
    queryKey: usageKey(scope, 'summary', summaryParams),
    queryFn: async () => {
      const res = await axios.get<LlmUsageSummary>(scopeURL(scope, '/summary'), {
        headers: { Accept: 'application/json' },
        params: summaryParams,
      })
      return res.data
    },
    enabled,
    refetchInterval,
    refetchIntervalInBackground: false,
    staleTime: 5 * 1000,
  })

  return {
    rows: listQuery.data,
    summary: summaryQuery.data,
    loading: listQuery.isLoading || summaryQuery.isLoading,
    isFetching: listQuery.isFetching || summaryQuery.isFetching,
    error: listQuery.error?.message ?? summaryQuery.error?.message ?? null,
    refetch: () => {
      void listQuery.refetch()
      void summaryQuery.refetch()
    },
  }
}

/** The filter dropdowns' values. Admin-only; the values are themselves disclosure. */
export const useLlmUsageDistinct = (dimension: string, enabled = true) => {
  const account = useAccount()
  return useQuery<string[], Error>({
    queryKey: ['llm-usage', 'distinct', dimension],
    queryFn: async () => {
      const res = await axios.get<string[]>(
        `${API_BASE_URL}/admin/llm-usage/distinct/${encodeURIComponent(dimension)}`,
        { headers: { Accept: 'application/json' } },
      )
      return res.data ?? []
    },
    enabled: enabled && !!account.user,
    staleTime: 60 * 1000,
  })
}

export default useLlmUsage
